import type { ParentType } from '@risksmart-app/domain/src/types/consts';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Hook to check if the current user can manage custom ribbons for a given parent type.
 *
 * Uses RBAC-based permission checking via Permit.io: returns true if the user has a
 * Manager role (e.g., RiskManager, ControlManager) with update permission for the
 * custom ribbon resource associated with the parent type.
 *
 * Follows the same pattern as useCanManageFormConfig.
 */
export const useCanManageCustomRibbon = (parentType: ParentType) => {
  const trpc = useTRPC();
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const { data: canManage = false, isLoading } = useQuery({
    ...trpc.frontend.customRibbon.canManageCustomRibbon.queryOptions({
      parentType,
    }),
    enabled: !!parentType && trpcEnabled,
  });

  return { canManage, loading: isLoading };
};
